import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import {
  CalendarCheck,
  CalendarClock,
  Check,
  CircleAlert,
  Clock3,
  Hand,
  Link,
  Link2Off,
  LoaderCircle,
  RotateCw,
  Send,
} from "lucide-react";
import { Dialog, DialogTrigger, Popover } from "react-aria-components";
import { cn } from "@/utils/helpers";
import type { CalendarPlatform } from "@/types/contentCalendar.type";
import { EmptyState, ErrorState, StateAction } from "../../components/ViewStates";
import { kitButtonClass } from "../../components/kit/Button";
import PlatformGlyph from "../../components/PlatformGlyph";
import { useCoShellPortal } from "../../CoShellPortalContext";
import { useScheduleUpcoming, useUpcomingDistribution, type UpcomingPost } from "../api/useScheduling";
import { clockLabel, dayLabel } from "../live/liveModel";
import { CHANNEL_NAME } from "./calendarModel";
import { FilterButton, MenuRow, menuPanel } from "./CalendarHeader";
import {
  isScheduled,
  needsYou,
  scheduleDays,
  scheduleRows,
  scheduleSentence,
  type ScheduleRow,
  type ScheduleState,
} from "./scheduleModel";

export type ScheduleShow = "all" | "needs-you" | "scheduled";

const CONNECTIONS_HREF = "/dashboard/settings?page=connections";

/** What the header shows for Schedule: the status line and the view's own filters. */
export interface ScheduleChrome {
  status: string;
  /** Desktop and tablet: Needs you, Scheduled, Channels (Filter / Calendar). */
  filters: ReactNode;
  /** Phone: All, Needs you, Scheduled (Filter chip · Phone). */
  chips: ReactNode;
}

/**
 * Schedule, the calendar's fourth view (Pen: Live / Publishing · 14-16):
 * every channel going live in the next 14 days, in the order it goes out,
 * grouped by day, with what each needs. Accounts that are not connected get
 * one banner above the list.
 */
export default function ScheduleView({
  brandId,
  layout,
  onOpenPost,
  onOpenMonth,
  renderHeader,
}: {
  brandId: string | null;
  layout: "desktop" | "tablet" | "phone";
  onOpenPost: (post: UpcomingPost) => void;
  /** Empty state: back to the month. */
  onOpenMonth: () => void;
  renderHeader: (chrome: ScheduleChrome) => ReactNode;
}) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useUpcomingDistribution(brandId);
  const schedule = useScheduleUpcoming(brandId);
  const [show, setShow] = useState<ScheduleShow>("all");
  const [channel, setChannel] = useState<CalendarPlatform | null>(null);
  const [now] = useState(() => Date.now());
  const phone = layout === "phone";
  const timeZone = data?.timezone ?? "UTC";
  const days = data?.days ?? 14;

  const rows = useMemo(() => scheduleRows(data), [data]);
  const needs = rows.filter(needsYou).length;
  const scheduled = rows.filter(isScheduled).length;
  const channels = useMemo(() => {
    const counts = new Map<CalendarPlatform, number>();
    for (const row of rows) counts.set(row.platform, (counts.get(row.platform) ?? 0) + 1);
    return [...counts.entries()].map(([platform, count]) => ({ platform, count }));
  }, [rows]);
  const shown = rows.filter(
    (row) =>
      (show === "all" || (show === "needs-you" ? needsYou(row) : isScheduled(row))) &&
      (!channel || row.platform === channel),
  );
  const groups = scheduleDays(shown, timeZone, now);
  const unconnected = connectList(rows, timeZone, now);

  const status = isLoading
    ? "Reading what goes live next…"
    : show === "needs-you"
      ? `${needs} of ${rows.length} ${rows.length === 1 ? "channel needs" : "channels need"} you before they can go live.`
      : show === "scheduled"
        ? `${scheduled} of ${rows.length} ${rows.length === 1 ? "channel is" : "channels are"} scheduled.`
        : scheduleSentence(rows, days);

  const toggle = (next: ScheduleShow) => setShow((current) => (current === next ? "all" : next));

  const filters = (
    <div className={cn("flex flex-wrap items-center", layout === "tablet" ? "gap-2" : "gap-2.5")}>
      <FilterButton label="Needs you" count={needs} active={show === "needs-you"} onPress={() => toggle("needs-you")} />
      <FilterButton label="Scheduled" count={scheduled} active={show === "scheduled"} onPress={() => toggle("scheduled")} />
      <ChannelFilter channels={channels} value={channel} onChange={setChannel} />
    </div>
  );
  const chips = (
    <div className="-mx-3 flex items-center gap-2 overflow-x-auto px-4 pb-3 [scrollbar-width:none]">
      <Chip label="All" count={rows.length} on={show === "all"} onPress={() => setShow("all")} />
      <Chip label="Needs you" count={needs} on={show === "needs-you"} onPress={() => toggle("needs-you")} />
      <Chip label="Scheduled" count={scheduled} on={show === "scheduled"} onPress={() => toggle("scheduled")} />
    </div>
  );

  const act = (row: ScheduleRow) => {
    if (row.state === "connect") {
      navigate(CONNECTIONS_HREF);
      return;
    }
    if (!row.publicationId || !row.publicationStatus) return;
    schedule.mutate({
      slotId: row.post.slot.id,
      publicationId: row.publicationId,
      previous: row.publicationStatus as never,
      retry: row.state === "failed",
    });
  };

  let body: ReactNode;
  if (isLoading) {
    body = <ScheduleSkeleton phone={phone} />;
  } else if (isError && !data) {
    body = (
      <ErrorState
        title="The schedule didn’t load"
        body="Nothing changed on your posts. Try again in a moment."
        onRetry={() => void refetch()}
      />
    );
  } else if (rows.length === 0) {
    // Schedule, nothing in the next 14 days.
    body = (
      <EmptyState
        className={phone ? "flex-1" : "mx-auto max-w-[560px] flex-1"}
        icon={<CalendarClock className="size-5" aria-hidden="true" />}
        title="Nothing is scheduled yet"
        body={`Scheduled and manual posts for the next ${days} days show here, in the order they go live.`}
        actions={<StateAction onClick={onOpenMonth}>Open the month</StateAction>}
      />
    );
  } else if (shown.length === 0) {
    body = (
      <EmptyState
        className="flex-1"
        icon={<CalendarClock className="size-5" aria-hidden="true" />}
        title={show === "needs-you" ? "Nothing needs you" : show === "scheduled" ? "Nothing is scheduled yet" : "Nothing on this channel"}
        body={`Every other channel in the next ${days} days is still here.`}
        actions={
          <StateAction
            onClick={() => {
              setShow("all");
              setChannel(null);
            }}
          >
            Show everything
          </StateAction>
        }
      />
    );
  } else {
    body = (
      <>
        {unconnected.length > 0 ? (
          <ConnectBanner list={unconnected} phone={phone} onConnect={() => navigate(CONNECTIONS_HREF)} />
        ) : null}
        {groups.map((group) => (
          <section key={group.key} className={cn("flex flex-col", phone ? "gap-3" : "gap-0.5")}>
            {/* Day header: Barlow Condensed 20/700, pad 22/0/8 (phone 8/0/0). */}
            <h2 className={cn("font-section text-[20px] font-bold leading-[1.2] text-co-text", phone ? "pt-2" : "pb-2 pt-[22px]")}>
              {group.label}
            </h2>
            {group.rows.map((row) =>
              phone ? (
                <PhoneRow key={row.key} row={row} timeZone={timeZone} onOpen={() => onOpenPost(row.post)} onAct={() => act(row)} />
              ) : (
                <DeskRow key={row.key} row={row} timeZone={timeZone} onOpen={() => onOpenPost(row.post)} onAct={() => act(row)} />
              ),
            )}
          </section>
        ))}
      </>
    );
  }

  return (
    <>
      {renderHeader({ status, filters, chips })}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          phone ? "-mx-3 gap-3 px-4 pb-[120px] pt-3 shadow-[inset_0_1px_0_var(--co-line)]" : "gap-0.5 pb-10",
          layout === "desktop" && "px-8",
          layout === "tablet" && "px-5",
        )}
      >
        {body}
      </div>
    </>
  );
}

// -------------------------------------------------------------------- banner

interface Unconnected {
  platform: CalendarPlatform;
  /** "today at 18:00", "Thu 10 Dec". */
  when: string;
}

/** Each platform with no account, at its first channel going out. */
function connectList(rows: ScheduleRow[], timeZone: string, now: number): Unconnected[] {
  const today = dayLabel(new Date(now).toISOString(), timeZone);
  const seen = new Map<CalendarPlatform, Unconnected>();
  for (const row of rows) {
    if (row.state !== "connect" || seen.has(row.platform)) continue;
    const day = dayLabel(row.at, timeZone);
    seen.set(row.platform, {
      platform: row.platform,
      when: day === today ? (row.dayOnly ? "today" : `today at ${clockLabel(row.at, timeZone)}`) : day,
    });
  }
  return [...seen.values()];
}

function joinWords(words: string[]): string {
  return words.length <= 1 ? (words[0] ?? "") : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** Status banner (Pen: 2 accounts aren’t connected): tonal, radius 16, 84 tall, pad 0/20, gap 16. */
function ConnectBanner({ list, phone, onConnect }: { list: Unconnected[]; phone: boolean; onConnect: () => void }) {
  const count = list.length;
  const title = count === 1 ? `${CHANNEL_NAME[list[0].platform]} isn’t connected` : `${count} accounts aren’t connected`;
  const meta = `${joinWords(list.map((entry) => `${CHANNEL_NAME[entry.platform]} for ${entry.when}`))}. Connect ${
    count === 1 ? "it" : "them"
  } once and everything goes live on time.`;
  const label = count === 1 ? "Connect" : count === 2 ? "Connect both" : "Connect all";
  return (
    <div className={phone ? "" : "pb-1 pt-5"}>
      <div
        role="status"
        className={cn(
          "flex rounded-panel bg-co-tonal",
          phone ? "flex-col gap-3 p-4" : "min-h-[84px] items-center gap-4 px-5 py-3",
        )}
      >
        <div className={cn("flex min-w-0 flex-1 gap-4", phone ? "items-start" : "items-center")}>
          <span className="grid size-9 shrink-0 place-items-center rounded-[4px] bg-co-tonal text-co-text">
            <Link2Off className="size-[17px]" aria-hidden="true" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-[16px] font-semibold leading-[1.25] text-co-text">{title}</p>
            <p className="text-[13.5px] leading-[1.4] text-co-text2">{meta}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onConnect}
          className={cn(kitButtonClass({ variant: "primary" }), "h-8 shrink-0 gap-2 px-3.5 text-[13px] font-medium", phone && "self-start")}
        >
          {label}
          <Link className="size-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------- rows

const STATE_WORDS: Record<ScheduleState, string> = {
  scheduled: "Scheduled",
  posting: "Posting",
  connect: "Not connected",
  "not-scheduled": "Not scheduled",
  manual: "Manual",
  failed: "Failed",
  live: "Live",
};

function StateMark({ row }: { row: ScheduleRow }) {
  const alert = row.state === "connect" || row.state === "failed";
  const Icon =
    row.state === "scheduled"
      ? CalendarCheck
      : row.state === "posting"
        ? LoaderCircle
        : row.state === "connect"
          ? Link2Off
          : row.state === "not-scheduled"
            ? Clock3
            : row.state === "manual"
              ? Hand
              : row.state === "failed"
                ? CircleAlert
                : Send;
  return (
    <span className="flex min-w-0 items-center gap-1.5" title={row.error ?? undefined}>
      {row.state === "live" ? (
        <span aria-hidden="true" className="mx-1 size-1.5 shrink-0 rounded-full bg-co-live" />
      ) : (
        <Icon
          className={cn("size-3.5 shrink-0", alert ? "text-co-alert-ink" : "text-co-muted", row.state === "posting" && "animate-spin")}
          aria-hidden="true"
        />
      )}
      <span className={cn("truncate text-[12.5px] leading-[1.4]", alert ? "text-co-alert-ink" : "text-co-text")}>
        {STATE_WORDS[row.state]}
      </span>
    </span>
  );
}

/** The row's one action: Connect, Schedule or Try again (Text action: 32 tall, 13/500, muted). */
function RowAction({ row, onAct }: { row: ScheduleRow; onAct: () => void }) {
  const action =
    row.state === "connect"
      ? { label: "Connect", Icon: Link }
      : row.state === "not-scheduled" && row.canSchedule && row.publicationId
        ? { label: "Schedule", Icon: CalendarCheck }
        : row.state === "failed" && row.publicationId
          ? { label: "Try again", Icon: RotateCw }
          : null;
  if (!action) return null;
  return (
    <button
      type="button"
      onClick={onAct}
      className="relative z-10 inline-flex h-8 items-center gap-1.5 rounded-pill px-1 text-[13px] font-medium text-co-muted outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
    >
      <action.Icon className="size-3.5" aria-hidden="true" />
      {action.label}
    </button>
  );
}

/** Plate media: the post's still, 36 by 48, radius 8. */
function Thumb({ row }: { row: ScheduleRow }) {
  return row.thumbUrl ? (
    <img src={row.thumbUrl} alt="" className="h-12 w-9 shrink-0 rounded-tip object-cover" loading="lazy" />
  ) : (
    <span aria-hidden="true" className="grid h-12 w-9 shrink-0 place-items-center rounded-tip bg-co-tonal text-co-muted">
      <PlatformGlyph platform={row.platform} className="size-3.5" />
    </span>
  );
}

function timeOf(row: ScheduleRow, timeZone: string): string {
  return row.dayOnly ? "–" : clockLabel(row.at, timeZone);
}

/** The open-post target: the title stretched over the whole row. */
function OpenTitle({ row, onOpen, className }: { row: ScheduleRow; onOpen: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "text-left text-[14px] font-medium leading-[1.4] text-co-text outline-none after:absolute after:inset-0 after:rounded-card focus-visible:after:outline-2 focus-visible:after:outline-co-accent",
        className,
      )}
    >
      {row.title}
    </button>
  );
}

/** Live / Schedule row: time 52, thumb, main, channel 24, status 220, who 150, action 104; gap 16, pad 12/16. */
function DeskRow({ row, timeZone, onOpen, onAct }: { row: ScheduleRow; timeZone: string; onOpen: () => void; onAct: () => void }) {
  return (
    <div className="relative flex items-center gap-4 rounded-card bg-co-surface px-4 py-3 transition-colors hover:bg-co-hover">
      <span className={cn("w-[52px] shrink-0 text-[14px] font-semibold leading-[1.4] tabular-nums", row.dayOnly ? "text-co-muted" : "text-co-text")}>
        {timeOf(row, timeZone)}
      </span>
      <Thumb row={row} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <OpenTitle row={row} onOpen={onOpen} className="line-clamp-2" />
        <span className="truncate text-[12px] leading-[1.4] text-co-muted">{row.meta}</span>
      </div>
      <span className="flex w-6 shrink-0 justify-center text-co-muted">
        <PlatformGlyph platform={row.platform} className="size-3.5" />
        <span className="sr-only">{CHANNEL_NAME[row.platform]}</span>
      </span>
      <span className="w-[220px] shrink-0 max-lg:w-[150px]">
        <StateMark row={row} />
      </span>
      <span className="w-[150px] shrink-0 truncate text-[12px] leading-[1.4] text-co-muted max-lg:hidden">{row.who ?? ""}</span>
      <span className="flex w-[104px] shrink-0 justify-end">
        <RowAction row={row} onAct={onAct} />
      </span>
    </div>
  );
}

/** Live / Schedule row · Phone: thumb, then the title over time, channel and status; gap 12, pad 12/14. */
function PhoneRow({ row, timeZone, onOpen, onAct }: { row: ScheduleRow; timeZone: string; onOpen: () => void; onAct: () => void }) {
  return (
    <div className="relative flex items-center gap-3 rounded-card bg-co-surface px-3.5 py-3">
      <Thumb row={row} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <OpenTitle row={row} onOpen={onOpen} className="line-clamp-2" />
        <span className="flex min-w-0 items-center gap-2">
          <span className={cn("text-[13px] font-semibold leading-[1.4] tabular-nums", row.dayOnly ? "text-co-muted" : "text-co-text")}>
            {timeOf(row, timeZone)}
          </span>
          <span className="flex w-4 shrink-0 justify-center text-co-muted">
            <PlatformGlyph platform={row.platform} className="size-3.5" />
            <span className="sr-only">{CHANNEL_NAME[row.platform]}</span>
          </span>
          <StateMark row={row} />
        </span>
      </div>
      <RowAction row={row} onAct={onAct} />
    </div>
  );
}

// -------------------------------------------------------------------- filters

function ChannelFilter({
  channels,
  value,
  onChange,
}: {
  channels: { platform: CalendarPlatform; count: number }[];
  value: CalendarPlatform | null;
  onChange: (next: CalendarPlatform | null) => void;
}) {
  const portal = useCoShellPortal();
  return (
    <DialogTrigger>
      <FilterButton label={value ? CHANNEL_NAME[value] : "Channels"} active={!!value} menu />
      <Popover
        placement="bottom end"
        offset={6}
        UNSTABLE_portalContainer={portal ?? undefined}
        className="co-cal-peek z-[var(--z-index-popover)] outline-none"
      >
        <Dialog aria-label="Filter by channel" className={cn(menuPanel, "w-[240px]")}>
          {({ close }) => (
            <>
              {channels.length === 0 ? <p className="px-2.5 py-2.5 text-[13px] text-co-muted">Nothing goes out yet</p> : null}
              {channels.map(({ platform, count }) => (
                <MenuRow
                  key={platform}
                  active={value === platform}
                  onPress={() => {
                    onChange(value === platform ? null : platform);
                    close();
                  }}
                  lead={<PlatformGlyph platform={platform} className="size-3.5 text-co-text2" />}
                  label={CHANNEL_NAME[platform]}
                  count={count}
                />
              ))}
              {value ? (
                <MenuRow
                  active={false}
                  onPress={() => {
                    onChange(null);
                    close();
                  }}
                  label="Every channel"
                />
              ) : null}
            </>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

/** Filter chip · Phone: 34 tall; on, the accent tint with its border. */
function Chip({ label, count, on, onPress }: { label: string; count: number; on: boolean; onPress: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPress}
      className={cn(
        "co-glass-control inline-flex h-[34px] shrink-0 items-center gap-1.5 rounded-pill px-3 text-[13px] outline-none",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
        on
          ? "!bg-co-accent-dim font-semibold text-co-text !shadow-[inset_0_0_0_1px_var(--co-accent-border)]"
          : "font-medium text-co-text2",
      )}
    >
      {on ? <Check className="size-3.5 text-co-accent" strokeWidth={2.25} aria-hidden="true" /> : null}
      {label}
      <span className={cn("text-[12px] font-normal tabular-nums", on ? "text-co-accent" : "text-co-muted")}>{count}</span>
    </button>
  );
}

// ------------------------------------------------------------------- skeleton

/** Two days of rows, shaped like the list. */
function ScheduleSkeleton({ phone }: { phone: boolean }) {
  return (
    <div aria-hidden="true" className={cn("flex flex-col", phone ? "gap-3" : "gap-0.5")}>
      {[3, 2].map((count, day) => (
        <div key={day} className={cn("flex flex-col", phone ? "gap-3" : "gap-0.5")}>
          <span className={cn("h-5 w-36 animate-pulse rounded-tag bg-co-tonal", phone ? "mt-2" : "mb-2 mt-[22px]")} />
          {Array.from({ length: count }, (_, index) => (
            <div key={index} className={cn("flex items-center rounded-card bg-co-surface", phone ? "gap-3 px-3.5 py-3" : "gap-4 px-4 py-3")}>
              {phone ? null : <span className="h-4 w-[52px] shrink-0 animate-pulse rounded-tag bg-co-tonal" />}
              <span className="h-12 w-9 shrink-0 animate-pulse rounded-tip bg-co-tonal" />
              <span className="flex min-w-0 flex-1 flex-col gap-2">
                <span className="h-3.5 w-3/5 animate-pulse rounded-tag bg-co-tonal" />
                <span className="h-3 w-2/5 animate-pulse rounded-tag bg-co-tonal" />
              </span>
              {phone ? null : <span className="h-3.5 w-[220px] shrink-0 animate-pulse rounded-tag bg-co-tonal max-lg:w-[150px]" />}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
